package com.appsyde.shared.presentation.screen.component.menu

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.State
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.appsyde.shared.getAccessToken
import com.appsyde.shared.backendBaseUrl
import kotlin.js.js
import com.appsyde.shared.modules.ai.domain.model.DetectedFieldV2
import com.appsyde.shared.modules.ticket.domain.model.Direction
import com.appsyde.shared.presentation.screen.component.Spacer
import com.appsyde.shared.presentation.screen.component.message.helper.formatSmartTime
import com.appsyde.shared.presentation.screen.component.message.mapper.GROUP_ORDER
import com.appsyde.shared.presentation.screen.component.message.mapper.canCopy
import com.appsyde.shared.presentation.screen.component.message.mapper.groupSoft
import com.appsyde.shared.presentation.screen.component.message.mapper.sidebarFieldOrder
import com.appsyde.shared.presentation.screen.component.message.mapper.icon
import com.appsyde.shared.presentation.screen.component.message.mapper.isPreuve
import com.appsyde.shared.presentation.screen.component.message.mapper.isYesValue
import com.appsyde.shared.presentation.screen.component.message.mapper.uiLabel
import com.appsyde.shared.presentation.screen.component.message.mapper.uiValue
import com.appsyde.shared.presentation.style.style.AppStylesLeftSidebar
import com.appsyde.shared.presentation.style.style.AppStylesLayout
import com.appsyde.shared.presentation.style.style.AppStylesRightSidebar
import com.appsyde.shared.presentation.style.style.AppStylesTicketsSidebar
import com.appsyde.shared.presentation.screen.component.menu.helper.copyToClipboard
import com.appsyde.shared.presentation.screen.component.menu.helper.isSingleLine
import com.appsyde.shared.presentation.screen.component.menu.helper.maskEmailForUi
import com.appsyde.shared.presentation.style.style.InsightStyles
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text



// ------------------------------------------------------------
// Sidebar
// ------------------------------------------------------------

@Composable
fun SideBarMenu(
    showClientInfoCard: Boolean,
    detected1: List<DetectedFieldV2>?,
    documents: List<SidebarDocument> = emptyList(),
    ticketId: String? = null,
    clientEmail: String? = null,
    clientName: String? = null,
) {
    Style(AppStylesLeftSidebar)
    Style(AppStylesRightSidebar)
    Style(AppStylesTicketsSidebar)

    Div(attrs = { classes(AppStylesLayout.sidebar, AppStylesLeftSidebar.sidebarGap) }) {

        if (!showClientInfoCard) return@Div

        // Carte : Client (en haut) + Données utiles + Documents — même fond que Ticket Contexte
        Div(attrs = { classes(AppStylesLeftSidebar.sidebarDossierCard, InsightStyles.sidebarRoot) }) {
            var documentsExpanded by remember { mutableStateOf(true) }

            Div(attrs = { classes(AppStylesLeftSidebar.sidebarDossierHeaderBlock) }) {
                Div(attrs = { classes(InsightStyles.panelTitleWrap, AppStylesLeftSidebar.sidebarDossierTitleInHeader) }) {
                    Span(attrs = { classes(  AppStylesLeftSidebar.sidebarCardKicker) }) { Text("Synthèse") }
                    Span(attrs = { classes(InsightStyles.panelTitle, AppStylesLeftSidebar.sidebarCardTitle) }) { Text("Dossier") }
                }
                // — Client (plus proche du titre) : nom, email, tél et adresse extraits ici, retirés de la liste
                    val clientPhone = remember(detected1) {
                        detected1.orEmpty().firstOrNull { it.key?.trim()?.lowercase() in setOf("customer_phone", "phone") }?.value?.trim()?.takeIf { it.isNotBlank() }
                    }
                    val clientAddress = remember(detected1) {
                        detected1.orEmpty().firstOrNull { it.key?.trim()?.lowercase() == "delivery_address" }?.value?.trim()?.takeIf { it.isNotBlank() }
                    }
                    if (clientEmail != null || clientName != null || clientPhone != null || clientAddress != null) {
                    Div(attrs = { classes(AppStylesTicketsSidebar.dossierSection, AppStylesLeftSidebar.sidebarClientBlock) }) {
                    Div(attrs = { classes(AppStylesTicketsSidebar.clientInfoGroup) }) {
                        Div(attrs = { classes(AppStylesTicketsSidebar.clientCardNameLight, AppStylesLeftSidebar.sidebarClientName) }) {
                            Text(clientName?.takeIf { it.isNotBlank() } ?: "Contact")
                        }
                        if (clientEmail != null) {
                            Div(attrs = { classes(AppStylesTicketsSidebar.clientCardEmailLight, AppStylesLeftSidebar.sidebarClientEmail) }) { Text(clientEmail) }
                        }
                        if (clientPhone != null) {
                            Div(attrs = { classes(AppStylesTicketsSidebar.clientCardEmailLight, AppStylesLeftSidebar.sidebarClientEmail) }) { Text(clientPhone) }
                        }
                        if (clientAddress != null) {
                            Div(attrs = { classes(AppStylesTicketsSidebar.clientCardEmailLight, AppStylesLeftSidebar.sidebarClientEmail) }) { Text(clientAddress) }
                        }
                    }
                }
                }
            }

            // — Mémoire (liste) — 24px sous le bloc client
            Div(attrs = { classes(AppStylesLeftSidebar.sidebarDossierSection, AppStylesLeftSidebar.sidebarDossierSectionAfterClient) }) {
                    // ✓ feedback for ~900ms
                    var copiedToken by remember { mutableStateOf<String?>(null) }
                    LaunchedEffect(copiedToken) {
                        if (copiedToken != null) {
                            delay(900)
                            copiedToken = null
                        }
                    }

                    // Hovered row token (to render the button only on hover)
                    var hoveredToken by remember { mutableStateOf<String?>(null) }

                    // ✅ V2 null-safe + drop empty values ; retirer email/nom s’ils sont identiques au bloc Client (évite doublon)
                    val grouped = remember(detected1, clientEmail, clientName) {
                        val cleaned = detected1
                            .orEmpty()
                            .mapNotNull { d ->
                                val k = d.key?.trim().orEmpty()
                                val kNorm = k.lowercase()
                                val vRaw = d.value?.trim().orEmpty()
                                if (k.isBlank() || vRaw.isBlank()) return@mapNotNull null
                                if (kNorm == "email" || kNorm == "customer_email") {
                                    if (clientEmail != null && vRaw.equals(clientEmail, ignoreCase = true)) return@mapNotNull null
                                }
                                if (kNorm == "customer_name" || kNorm == "client") {
                                    if (clientName != null && vRaw.trim().equals(clientName.trim(), ignoreCase = true)) return@mapNotNull null
                                }
                                if (kNorm in setOf("customer_phone", "phone", "delivery_address")) return@mapNotNull null
                                if (kNorm == "delivery_recipient" && clientName != null && vRaw.trim().equals(clientName.trim(), ignoreCase = true)) return@mapNotNull null
                                val v = if (kNorm == "email") vRaw.maskEmailForUi().orEmpty() else vRaw
                                d.copy(key = k, value = v)
                            }

                        val byGroup = cleaned.groupBy { it.groupSoft() }
                        GROUP_ORDER.mapNotNull { g ->
                            val items = (byGroup[g].orEmpty()).sortedBy { it.sidebarFieldOrder() }
                            if (items.isEmpty()) null else g to items
                        }
                    }

                    val flat = remember(grouped) { grouped.flatMap { it.second } }
                    val commandeItems = remember(flat) { flat.filter { !it.isPreuve() } }
                    val preuveItems = remember(flat) { flat.filter { it.isPreuve() } }
                    val useCategories = commandeItems.isNotEmpty() || preuveItems.isNotEmpty()
                    var commandeExpanded by remember { mutableStateOf(true) }
                    var preuvesExpanded by remember { mutableStateOf(true) }

                    @Composable
                    fun RenderMemoryItems(items: List<DetectedFieldV2>) {
                        val lastIdx = items.lastIndex
                        items.forEachIndexed { index, detected ->
                            val singleLine = detected.isSingleLine()
                            val canCopy = detected.canCopy()
                            val k = detected.key?.trim().orEmpty()
                            val v = detected.uiValue()
                            val token = "$k:$v"
                            val showCheck = copiedToken == token
                            val shouldRenderCopyButton = canCopy && (hoveredToken == token || showCheck)
                            val isLast = index == lastIdx
                            val rowClasses = buildList {
                                add(InsightStyles.infoRow)
                                add(AppStylesLeftSidebar.sidebarMemoryRow)
                                if (isLast) add(InsightStyles.infoRowLast)
                                if (canCopy) add(InsightStyles.copyableRow)
                            }
                            Div(attrs = {
                                classes(
                                    *rowClasses.toTypedArray(),
                                    if (singleLine) InsightStyles.infoRowWrapSingle else InsightStyles.infoRowWrap
                                )
                                if (canCopy) {
                                    onMouseEnter { hoveredToken = token }
                                    onMouseLeave { if (hoveredToken == token) hoveredToken = null }
                                    onClick { copyToClipboard(v); copiedToken = token }
                                    attr("role", "button")
                                    attr("tabindex", "0")
                                    onKeyDown { if (it.key == "Enter" || it.key == " ") { copyToClipboard(v); copiedToken = token } }
                                }
                            }) {
                               // Span(attrs = { classes(InsightStyles.infoIcon, AppStylesLeftSidebar.sidebarMemoryIcon) }) { Text(detected.icon()) }
                                val labelText = detected.uiLabel()
                                val labelLong = labelText.length > 18
                                val labelMedium = labelText.length > 15 && !labelLong
                                if (labelText.isNotBlank()) {
                                    Span(attrs = {
                                        classes(
                                            InsightStyles.infoLabel,
                                            AppStylesLeftSidebar.sidebarMemoryLabel,
                                            when {
                                                labelLong -> InsightStyles.infoLabelTwoLines
                                                labelMedium -> InsightStyles.infoLabelEllipsis
                                                else -> InsightStyles.infoLabelShort
                                            }
                                        )
                                    }) { Text(labelText) }
                                }
                                val sourceBadge = when (detected.source?.trim()?.lowercase()) {
                                    "agent_entered" -> "✔ "
                                    "agent_confirmed" -> "✔ "
                                    else -> null
                                }
                                if (singleLine) {
                                    Div(attrs = { classes(InsightStyles.infoRowRightBlock) }) {
                                        Span(attrs = {
                                            classes(
                                                InsightStyles.infoValue,
                                                AppStylesLeftSidebar.sidebarMemoryValue,
                                                InsightStyles.infoValueSingleLine
                                            )
                                        }) { Text(v) }
                                        if (sourceBadge != null) {
                                            Span(attrs = { classes(InsightStyles.sourceBadge) }) { Text(sourceBadge) }
                                        }
                                        if (shouldRenderCopyButton) {
                                            Button(attrs = {
                                                title(if (showCheck) "Copié" else "Copier")
                                                classes(InsightStyles.copyButton)
                                                onClick { it.stopPropagation(); copyToClipboard(v); copiedToken = token }
                                            }) { Text(if (showCheck) "✓" else "📋") }
                                        }
                                    }
                                } else {
                                    Span(attrs = {
                                        classes(
                                            InsightStyles.infoValue,
                                            AppStylesLeftSidebar.sidebarMemoryValue,
                                            InsightStyles.infoValueMultiLine
                                        )
                                    }) { Text(v) }
                                    if (sourceBadge != null) {
                                        Span(attrs = { classes(InsightStyles.sourceBadge) }) { Text(sourceBadge) }
                                    }
                                    if (shouldRenderCopyButton) {
                                        Button(attrs = {
                                            title(if (showCheck) "Copié" else "Copier")
                                            classes(InsightStyles.copyButton)
                                            onClick { it.stopPropagation(); copyToClipboard(v); copiedToken = token }
                                        }) { Text(if (showCheck) "✓" else "📋") }
                                    }
                                }
                            }
                        }
                    }

                    Div(attrs = { classes(InsightStyles.infoList) }) {
                        if (!useCategories) {
                            RenderMemoryItems(flat)
                        } else {
                            if (commandeItems.isNotEmpty()) {
                                 Div(attrs = { classes(AppStylesLeftSidebar.sidebarDossierSection) }) {
                                    Div(attrs = {
                                        classes(AppStylesLeftSidebar.sidebarDossierSectionTitleRow)
                                        attr("style", "cursor: pointer")
                                        onClick { commandeExpanded = !commandeExpanded }
                                    }) {
                                        Span(attrs = { classes(InsightStyles.copilotSectionTitleCollectInline) }) { Text("Informations (${commandeItems.size})") }
                                        Span(attrs = { classes(InsightStyles.collapsibleSectionChevron) }) { Text(if (commandeExpanded) "▼" else "▶") }
                                    }
                                    Div(attrs = {
                                        classes(
                                            AppStylesLeftSidebar.dossierSectionBodyWrap,
                                            if (commandeExpanded) AppStylesLeftSidebar.dossierSectionBodyExpanded else AppStylesLeftSidebar.dossierSectionBodyCollapsed
                                        )
                                    }) {
                                        Div(attrs = { classes(AppStylesLeftSidebar.dossierSectionBodyInner) }) {
                                            Div(attrs = { classes(InsightStyles.collapseDivider) })
                                            RenderMemoryItems(commandeItems)
                                        }
                                    }
                                }
                            }
                            if (preuveItems.isNotEmpty()) {
                                Div(attrs = { classes(AppStylesLeftSidebar.sidebarDossierSection, AppStylesLeftSidebar.sidebarDossierSectionAfterSection) }) {
                                    Div(attrs = {
                                        classes(AppStylesLeftSidebar.sidebarDossierSectionTitleRow)
                                        attr("style", "cursor: pointer")
                                        onClick { preuvesExpanded = !preuvesExpanded }
                                    }) {
                                        Span(attrs = { classes(InsightStyles.copilotSectionTitleCollectInline) }) { Text("Preuves (${preuveItems.size})") }
                                        Span(attrs = { classes(InsightStyles.collapsibleSectionChevron) }) { Text(if (preuvesExpanded) "▼" else "▶") }
                                    }
                                    Div(attrs = {
                                        classes(
                                            AppStylesLeftSidebar.dossierSectionBodyWrap,
                                            if (preuvesExpanded) AppStylesLeftSidebar.dossierSectionBodyExpanded else AppStylesLeftSidebar.dossierSectionBodyCollapsed
                                        )
                                    }) {
                                        Div(attrs = { classes(AppStylesLeftSidebar.dossierSectionBodyInner) }) {
                                            Div(attrs = { classes(InsightStyles.collapseDivider) })
                                            RenderMemoryItems(preuveItems)
                                        }
                                    }
                                }
                            }
                        }
                    }
            }

            if (documents.isNotEmpty() && ticketId != null) {
                Div(attrs = { classes(AppStylesLeftSidebar.sidebarDossierSection, AppStylesLeftSidebar.sidebarDossierSectionAfterSection) }) {
                    Div(attrs = {
                        classes(AppStylesLeftSidebar.sidebarDossierSectionTitleRow)
                        attr("style", "cursor: pointer")
                        onClick { documentsExpanded = !documentsExpanded }
                    }) {
                        Span(attrs = { classes(InsightStyles.copilotSectionTitleCollectInline) }) { Text("Documents (${documents.size})") }
                        Span(attrs = { classes(InsightStyles.collapsibleSectionChevron) }) { Text(if (documentsExpanded) "▼" else "▶") }
                    }
                    Div(attrs = {
                        classes(
                            AppStylesLeftSidebar.dossierSectionBodyWrap,
                            if (documentsExpanded) AppStylesLeftSidebar.dossierSectionBodyExpanded else AppStylesLeftSidebar.dossierSectionBodyCollapsed
                        )
                    }) {
                        Div(attrs = { classes(AppStylesLeftSidebar.dossierSectionBodyInner) }) {
                            Div(attrs = { classes(InsightStyles.collapseDivider) })
                            Div(attrs = { classes(AppStylesRightSidebar.documentsCard) }) {
                            val baseUrl = backendBaseUrl().trimEnd('/')
                            documents.forEach { doc ->
                            val accessToken = getAccessToken()
                            val url = "$baseUrl/tickets/$ticketId/messages/${doc.messageId}/attachments/${doc.attachment.id}" + if (!accessToken.isNullOrBlank()) "?access_token=" + (js("encodeURIComponent") as (String) -> String)(accessToken) else ""
                            A(
                                href = url,
                                attrs = {
                                    classes(AppStylesRightSidebar.documentItem)
                                    attr("target", "_blank")
                                    attr("rel", "noopener noreferrer")
                                    if (!doc.attachment.mimeType.startsWith("image/") && doc.attachment.mimeType != "application/pdf") {
                                        attr("download", doc.attachment.filename)
                                    }
                                }
                            ) {
                                Div(attrs = { classes(AppStylesRightSidebar.documentItemIconsRow) }) {
                                    Span(attrs = { classes(AppStylesRightSidebar.documentIconsGroup) }) {
                                        Span(attrs = { classes(AppStylesRightSidebar.documentDirectionIcon) }) {
                                            Text(when (doc.direction) {
                                                Direction.INBOUND -> "⬇"
                                                Direction.OUTBOUND -> "⬆"
                                                else -> "·"
                                            })
                                        }
                                        Span(attrs = { classes(AppStylesRightSidebar.documentIcon) }) {
                                            Text(documentIcon(doc.attachment.mimeType))
                                        }
                                    }
                                }
                                Span(attrs = { classes(AppStylesRightSidebar.documentTitle, AppStylesLeftSidebar.sidebarDocumentTitle) }) {
                                    Text(shortTitle(doc.attachment.filename))
                                }
                                Span(attrs = { classes(AppStylesRightSidebar.documentDate) }) {
                                    Text(formatSmartTime(doc.sentAtMs))
                                }
                            }
                        }
                    }
                    }
                    }
                }
            }
        }
    }
}
